import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { AudioMessage as AudioMessageModal } from '../../modals/audioMessage';
import type { AnnouncementPayloadT } from '../../types';

import { useAnnouncementName } from './callback';

export type HangupAfterAudioMessage =
  | {
      hangUpAfterAnnouncement: true;
      afterAnnouncementNodeId: null;
    }
  | {
      hangUpAfterAnnouncement: false;
      afterAnnouncementNodeId: string;
    };

export type Payload = {
  announcement: AnnouncementPayloadT;
} & HangupAfterAudioMessage;

type AudioMessageNodeAttributes = GenericNodeAttributes<
  Payload,
  PhoneExtraData
>;
type AudioMessageNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: AudioMessageNodeAttributes['translatePayload'] = (
  node,
) => {
  const { hangUpAfterAnnouncement, afterAnnouncementNodeId } = node.payload;

  return !hangUpAfterAnnouncement
    ? [
        {
          id: uuid(),
          connection: afterAnnouncementNodeId || uuid(),
          label: 'arrow',
        },
      ]
    : [];
};

const updateConnection: AudioMessageNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = {
    ...node.payload,
    hangUpAfterAnnouncement: false as const,
    afterAnnouncementNodeId: connectionId,
  };

  return { ...node, payload };
};

export const AudioMessageNodeAttributes: AudioMessageNodeAttributes = {
  iconName: 'announcement',
  label: 'Audio message',
  helpText:
    'Record or upload an audio message that’ll play when your customers call you (e.g. informing them when you’re closed for the holidays, asking to have an order number ready, etc.).',
  color: hexStringToNumber(flowNodeColor.red),
  translatePayload,
  updateConnection,
  NodeContent: AudioMessageNode,
  ActionModal: AudioMessageModal,
};

export function AudioMessageNode({
  node,
  context,
}: AudioMessageNodeProps): JSX.Element {
  const { extraData } = context;
  const { payload } = node;

  const announcement = useAnnouncementName(payload.announcement, extraData);

  return (
    <TextContent>
      {useMemo(
        () => (
          <>
            <TextEllipsis>
              <Text weight="normal" text="Audio file" />
              <Text weight="semibold" text={announcement} />
            </TextEllipsis>
          </>
        ),
        [announcement],
      )}
    </TextContent>
  );
}
